import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {DcpApiService, HttpRequestOptions} from '@dcp/configurator';
import {B2BUser, BaseSiteService, EventService, User} from '@spartacus/core';
import {UserAccountChangedEvent, UserAccountFacade} from '@spartacus/user/account/root';
import {combineLatest, Observable, ReplaySubject, Subject} from 'rxjs';
import {concatMap, switchMap, take, tap} from 'rxjs/operators';
import {AddressUsage} from '../model/order/dcp-order-entry.model';
import {CompanyType} from '../model/user/dcp-user';
import {DcpKymaService} from './dcp-kyma.service';

export interface DcpCustomerRegistrationAddressModel {
  addressNumber?: string;
  lineOfBusiness?: string;
  company?: string;
  user?: string;
  street: string;
  city: string;
  postalCode: string;
  line2?: string;
  countryCode?: string;
  regionCode?: string;
  firstName?: string;
  lastName?: string;
  externalAddressNumber?: string;
}

export interface DcpCustomerRegistrationModel {
  b2bUnitUid: string;
  b2bCustomerUid: string;
  customerId: string;
  addresses: DcpCustomerRegistrationAddressModel[];
  group?: string;
  sapAccountId?: string;

}

@Injectable({
  providedIn: 'root'
})
export class DcpCustomerRegistrationDecoratorService {

  readonly DEPARTMENT_CODE = '0003';
  private newlyRegisteredContacts = new Subject();
  private updatedContacts = new Subject();

  public updateTaxIdSubject: ReplaySubject<string> = new ReplaySubject<string>(1);

  constructor(
    private http: HttpClient,
    private kymaService: DcpKymaService,
    private apiService: DcpApiService,
    private baseSiteService: BaseSiteService,
    private userFacade: UserAccountFacade,
    private eventService: EventService,
  ) {
  }

  updateB2BUnit(payload: DcpCustomerRegistrationModel): Observable<any> {
    return this.apiService.callKyma('registration', 'register', {}, {body: payload}, 'POST');
  }

  /**
   * get customer from CRM
   */
  getCustomer(scimUserId: string): Observable<DcpCustomerRegistrationModel> {
    return this.kymaService.prepareAuthConfig().pipe(
      concatMap(
        (config: HttpRequestOptions) =>
          this.http.get<DcpCustomerRegistrationModel>(
            `${this.kymaService.getUrl('registration')}/customer/${scimUserId}`,
            config,
          )
      )
    );
  }

  getNewlyRegisteredContacts(): Observable<any> {
    return this.newlyRegisteredContacts.asObservable();
  }

  getUpdatedContacts(): Observable<any> {
    return this.updatedContacts.asObservable();
  }

  updateTaxConfig(body: any): Observable<any> {
    return combineLatest([
      this.getCurrentUser(),
      this.baseSiteService.getActive()
    ]).pipe(
      take(1),
      switchMap(([user, baseSite]: [User, string]) => {
        const pathVariables = {
          baseSite,
          userId: user.uid,
          b2bUnitId: user.orgUnit.uid
        };
        this.updateTaxIdSubject.next(body.taxId);
        return this.apiService.callKyma('registration', 'updateTaxConfig', pathVariables, {body}, 'PATCH');
      })
    );
  }

  registerNewContact(firstName: string, lastName: string, email: string, role: AddressUsage, isDepartment: boolean): Observable<any> {
    return combineLatest([
      this.getCurrentUser(),
      this.baseSiteService.getActive()
    ]).pipe(
      take(1),
      switchMap(([user, baseSite]: [User, string]) => {
        const pathVariables = {
          baseSite,
          userId: user.uid,
          b2bUnitId: user.orgUnit.uid
        };
        const body = {
          customerId: user.customerId,
          firstname: firstName,
          lastname: lastName,
          email,
          role,
          ...(isDepartment && {title: this.DEPARTMENT_CODE})
        };
        return this.apiService.callKyma('registration', 'newContactOrAddress', pathVariables, {body}, 'POST').pipe(
          tap((newContact) => {
            this.newlyRegisteredContacts.next(newContact);
          }));
      })
    );
  }

  updateContact(contact: B2BUser, isDepartment: boolean): Observable<any> {
    return this.baseSiteService.getActive()
      .pipe(
        take(1),
        switchMap(baseSite => {
          const pathVariables = {
            baseSite,
            userId: contact.uid,
            b2bUnitId: contact?.orgUnit?.uid || contact?.defaultB2BUnit?.uid
          };
          const body = {
            customerId: contact.customerId || contact.customerID,
            uid: contact.uid,
            firstname: contact.firstName,
            lastname: contact.lastName,
            email: contact.email,
            role: AddressUsage.IT_CONTACT,
            ...(isDepartment && {title: this.DEPARTMENT_CODE})
          };
          return this.apiService.callKyma('registration', 'newContactOrAddress', pathVariables, {body}, 'PATCH').pipe(
            tap(updatedContact => {
              this.updatedContacts.next(updatedContact);
            }));
        })
      );
  }

  /**
   * assign 'organization' or 'individual' group to b2b unit
   */
  setCompanyType(type: CompanyType): Observable<any> {
    return combineLatest([
      this.userFacade.get(),
      this.baseSiteService.getActive()
    ]).pipe(
      take(1),
      switchMap(([user, baseSite]: [User, string]) => {
        const pathVariables = {
          baseSite,
          userId: user.uid,
          b2bUnitId: user.orgUnit.uid
        };
        const body = {
          customerId: user.customerId,
          scimUserId: user.scimUserId,
          group: type,
        };
        return this.apiService.callKyma('registration', 'updateB2bUnit', pathVariables, {body}, 'PATCH');
      })
    );
  }

  /**
   * get current updated user from commerce and sets the retrieved user id to the userIdService to
   * prevent the failed dependency error while the unreplicated customer replicates during registration of
   * new contact
   */
  getCurrentUser(): Observable<User> {
    return this.apiService.callOcc('currentUser', {}, {}, 'GET');
  }

  getCurrentUserWithJson(): Observable<User> {
    return this.apiService.callOcc('currentUserWithJson', {}, {}, 'GET');
  }

  autoRegisterUser(userId: string, scimUserId: string): Observable<any> {
    return this.apiService.callKyma('bp-registration',
      'bp-register',
      {userId, scimUserId},
      {},
      'POST').pipe(
      tap(() => this.reloadCustomer())
    );
  }

  reloadCustomer() {
    this.eventService.dispatch(
      {user: {}},
      UserAccountChangedEvent
    );
  }

}
